import React, { useState } from 'react';
import { IStorageUnit } from '../../../@types/model/masterData/storageUnit/storageUnit';
import moment from 'moment';
import { DateHelper, DateTimeInput, PillButton, ProjectDialog } from '@zz2/zz2-ui';
import { Button, TextField, Typography } from '@mui/material';
import ColdStorageHttpService from '../../../services/http/coldStorage/coldStorageHttpService';
import { IEndColdStorageUpsert } from '../../../@types/model/coldStorage/endColdStorageUpsert';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { IApiError } from '../../../@types/other';
import { generalShowErrorSnackbar } from '../../../store/general/Functions';
import { IColdStorage } from '../../../@types/model/coldStorage/coldStorage';

interface IColdStorageEndScheduleDialogProps {
    isLoading : boolean;
    isOpen : boolean;
    selectedStorageUnit : IStorageUnit;
    latestColdStorageSession : IColdStorage | null;
    setIsLoading : (value : boolean) => void;
    onClose : (refresh : boolean) => void;
}

const ColdStorageEndScheduleDialog = (props : IColdStorageEndScheduleDialogProps) => {

    const [selectedEndDateTime, setSelectedEndDateTime] = useState<moment.Moment | null>(moment().local());

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const onSubmit = async () => {
        if (!selectedEndDateTime) return;

        try {
            props.setIsLoading(true);

            const upsert : IEndColdStorageUpsert = {
                storageUnitId: props.selectedStorageUnit.id,
                endedOn: selectedEndDateTime,
            };

            await ColdStorageHttpService.end(upsert);

            onClose(true);
        } catch (error : unknown) {
            const apiError = error as IApiError | null;
            generalShowErrorSnackbar(apiError?.data.Message ?? 'An error occurred while ending cold storage data.');
        }
        finally {
            props.setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const onClose = (refresh : boolean) => {
        setSelectedEndDateTime(null);
        props.onClose(refresh);
    };

    const onEndDateChange = (value : moment.Moment | null) => {
        setSelectedEndDateTime(value);
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/
    return (
        <ProjectDialog
            title={`End Cold Storage Session - ${props.selectedStorageUnit.code}`}
            isLoadingCircular={props.isLoading}
            isOpen={props.isOpen}
            onClose={() => onClose(false)}
            maxWidth={'sm'}
        >
            <div className={'fdc p20'}>
                <Typography>You are about to end the current cold storage session. Are you sure?</Typography>
                <div className={'h20'}/>
                {props.latestColdStorageSession &&
                    <TextField
                        label={'Start Date/Time'}
                        value={DateHelper.formatDateUtc(props.latestColdStorageSession.startedOn)}
                        // value={moment.utc(props.latestColdStorageSession.startedOn).local().format('YYYY-MM-DD HH:mm:ss')}
                        disabled
                        fullWidth
                        variant={'outlined'}
                        size={'small'}
                    />
                }
                <div className={'h20'}/>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateTimeInput
                        label={'End Date/Time'}
                        onDateChange={onEndDateChange}
                        value={selectedEndDateTime}
                    />
                </LocalizationProvider>
                <div className={'fdr flx1 aic jcfe mt20'}>
                    <Button
                        className={'cpd mr20'}
                        type={'button'}
                        onClick={() => onClose(false)}
                    >
                        CANCEL
                    </Button>
                    <PillButton
                        className={'pl30 pr30'}
                        text={'SAVE'}
                        size={'small'}
                        onClick={onSubmit}
                        color={'secondary'}
                        disabled={props.isLoading || !selectedEndDateTime}
                    />
                </div>
            </div>
        </ProjectDialog>
    );
};

export default ColdStorageEndScheduleDialog;